import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IItemCreationSubsectionInputProps,
  ItemCreationSubsection,
  TextEditor,
} from '~/Components';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { makeStyles } from '~/UIKit/Style';
import { ExtendedVoD } from './ExtendedVoD.model';
import { useRequestCategoryList } from '~/requests/eventService';

const useStyles = makeStyles(() => ({
  container: {
    paddingBottom: 30,
  },
  textEditor: {
    marginTop: 80,
  },
}));

export const VideoOnDemandDetailsForm = (video: ExtendedVoD) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const boundariesElement = useRef(null);
  const { data: categoryList = [], isLoading } = useRequestCategoryList();

  const categoryOptions = categoryList.map((category) => ({
    label: category.name,
    value: category.id,
  }));
  const VODFormFields: IItemCreationSubsectionInputProps<Partial<ExtendedVoD>>[] = [
    {
      label: `${t('app.vod.videoName')}`,
      placeholder: t('app.vod.videoNamePlaceholder'),
      type: ItemCreationValidInput.TEXT,
      name: 'name',
      hasAnError: video?.name?.length === 0,
      InputProps: { inputProps: { maxLength: 100 } },
    },
    {
      label: `${t('app.vod.sportCategory')}`,
      placeholder: t('app.vod.sportCategoryPlaceholder'),
      type: ItemCreationValidInput.SELECT,
      name: 'categoryId',
      hasAnError: video?.categoryId === 0,
      disabled: isLoading,
      options: categoryOptions,
    },
    {
      label: `${t('app.vod.status')}`,
      placeholder: t('app.vod.statusPlaceholder'),
      type: ItemCreationValidInput.SELECT,
      name: 'state',
      options: [
        { label: t('app.vod.statusDraft'), value: 'draft' },
        { label: t('app.vod.statusOnline'), value: 'online' },
      ],
    },
    {
      label: `${t('app.vod.trainers')}`,
      placeholder: t('app.vod.trainersPlaceholder'),
      type: ItemCreationValidInput.TRAINER_SELECTOR,
      name: 'trainerIds',
      hasAnError: video?.trainerIds?.length === 0,
      InputProps: { inputProps: { maxLength: 100 } },
    },
    {
      label: `${t('app.vod.language')}`,
      placeholder: t('app.vod.langaugePlaceholder'),
      type: ItemCreationValidInput.SELECT,
      name: 'language',
      options: [
        { label: t('app.vod.languageEnglish'), value: 'en' },
        { label: t('app.vod.languageGerman'), value: 'de' },
        { label: t('app.vod.languageFrench'), value: 'fe' },
        { label: t('app.vod.languageSpanish'), value: 'es' },
        { label: t('app.vod.languagePortuguese'), value: 'pr' },
        { label: t('app.vod.languageDutch'), value: 'nl' },
        // Add more languages as needed
      ],
    },
  ];
  const VODFormFieldsTextEditor: IItemCreationSubsectionInputProps<Partial<ExtendedVoD>>[] = [
    {
      customElement: (
        <TextEditor
          className={classes.textEditor}
          noToolbarSettings
          title={t('app.vod.aboutVideo')}
          fieldName="descriptions"
          tooltip={{
            title: t('app.vod.descriptionTooltip'),
          }}
          valuesAsArray={false}
          isLanguageSelectorAlwaysOn
        />
      ),
      customElementFullSection: true,
    },
  ];

  return (
    <div ref={boundariesElement} className={classes.container}>
      <ItemCreationSubsection items={VODFormFields} noBorder />
      <ItemCreationSubsection items={VODFormFieldsTextEditor} noBorder />
    </div>
  );
};
